import Decimal from "break_infinity.js";

export interface SupporterShop {
  coins: number;
  producerEffectiveness: number;
  producerSpeed: number;
  moleculeEffectiveness: number;
}

export interface Game {
  producers: Producer[];
  producerStrengthening: ProducerStrengthening;
  strengtheningBoost: StrengtheningBoost;
  unlockedAchievementIds: Set<number>;
  settings?: Settings;
  timestamp?: number;
  molecule: Molecule;
  producerAutoBuyTime: number;
  createdAt: number;
  last10MoleculeCreations: MoleculeCreation[];
  neutronState: NeutronState;
}

export interface NeutronState {
  timeMultiplier: number;
  multiplierMultiplier: number;
  state: "Neutron" | "Electron" | "Proton";
}
export interface Settings {}

export interface Molecule {
  value: Decimal;
  multiplier: number;
  unlocked: boolean;
  graphNodes: {
    [key: string]: number;
  };
  creations: number;
  startedAt: number;
}

export interface MoleculeCreation {
  startedAt: number;
  endedAt: number;
  molecules: Decimal;
}

export interface Producer {
  value: Decimal;
  upgradeLevel: number;
  name: string;
  alreadySeen: boolean;
  layerLevel: number;
  productName: string;
  productShortName: string;
  unlocked: boolean;
  produceTime: number; //in ms (1000ms = 1s)
  automateCollect: boolean;
  automateCollectDisabled: boolean;
  automateBuy: boolean;
  multiplier: Decimal;
  speedUpgradeLevel: number;
  shortName?: string;
  costScaling: number;
  baseCost: number;
  automateBuyBought: boolean;
}

export interface ProducerStrengthening {
  upgradeLevel: number;
  multiplier: Decimal;
}

export interface StrengtheningBoost {
  alreadySeen: boolean;
  upgradeLevel: number;
}

export interface OfflineDiff {
  producers: { name: string; diff: Decimal }[];
}

export function createProducer(props: {
  layerLevel: number;
  unlocked: boolean;
  costScaling: number;
  baseCost: number;
  alreadySeen?: boolean;
  value?: Decimal;
}): Producer {
  return {
    value: props.value ? props.value : new Decimal(0),
    upgradeLevel: 0,
    name: producerNames[props.layerLevel],
    layerLevel: props.layerLevel,
    productName:
      props.layerLevel > 0 ? producerNames[props.layerLevel - 1] : "None",
    unlocked: props.unlocked,
    produceTime: producerTimes[props.layerLevel],
    automateCollect: false,
    multiplier: new Decimal(1),
    speedUpgradeLevel: 0,
    shortName: producerShortNames[props.layerLevel],
    productShortName:
      props.layerLevel > 0 ? producerShortNames[props.layerLevel - 1] : "None",
    costScaling: props.costScaling,
    baseCost: props.baseCost,
    alreadySeen: props.alreadySeen ? props.alreadySeen : false,
    automateCollectDisabled: false,
    automateBuy: false,
    automateBuyBought: false,
  };
}

export function createGame(
  producers: Producer[],
  producerStrengthening: ProducerStrengthening,
  strengtheningBoost: StrengtheningBoost,
  unlockedAchievementIds: Set<number>,
  molecule: Molecule,
  producerAutoBuyTime: number,
  createdAt: number,
  settings?: Settings
): Game {
  return {
    producers: producers,
    producerStrengthening: producerStrengthening,
    strengtheningBoost: strengtheningBoost,
    unlockedAchievementIds: unlockedAchievementIds,
    settings: settings,
    molecule: molecule,
    producerAutoBuyTime: producerAutoBuyTime,
    createdAt: createdAt,
    last10MoleculeCreations: [],
    neutronState: {
      timeMultiplier: 1,
      multiplierMultiplier: 1,
      state: "Neutron",
    },
  };
}

export function checkIfMoleculeUnlocked(moleculeName: string, game: Game) {
  return Object.keys(game.molecule.graphNodes).includes(moleculeName);
}

export const producerNames: string[] = [
  "Particle",
  "Neutron",
  "Atom",
  "Microbot",
  "Weak AI",
  "Nanobot",
  "Strong AI",
  "Quantum Assembler",
  "Super AI",
  "Quantum Engineer",
];

export const producerTimes: number[] = [
  0,
  1000, // producer1
  2000, // producer2
  5000, // producer3
  10000, // producer4
  20000, // producer5
  40000, // producer6
  90000, // producer7
  200000, // producer8
  500000, // producer9
];
export const producerShortNames: string[] = [
  "P",
  "N",
  "A",
  "M-Bot",
  "W AI",
  "N-Bot",
  "Str AI",
  "Q. Assem.",
  "Sp AI",
  "Q E",
];

export enum BuySetting {
  BuyOne = "Single",
  BuyUntilTen = "Until 10",
  //BuyMax = "max",
}

export const times10Multiplier: number = 5;
export const times100Multiplier: number = 10;

export const startParticleValue: number = 10;

export const particlesPerMolecule: number = 1e100;

export const neededPsForSb: number = 5;

export const psStartMultiplier: Decimal = new Decimal(2.5);

export const speedFactor: number = 0.98;
